import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Stack,
  alpha,
  useTheme,
  Button,
  Alert,
} from '@mui/material';
import {
  CheckCircleOutline as ApproveIcon,
  BlockOutlined as RejectIcon,
  EditOutlined as EditIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import reviewQueueService, {
  ReviewEntityType,
  ReviewQueueItem,
  ReviewQueueSummary,
  REVIEW_TYPE_LABELS,
  REJECT_SUPPORTED,
  getEditUrl,
} from '../../../services/reviewQueueService';
import type { PossibleMatch } from '../../../services/reviewQueueService';

// ── Type chip colours ─────────────────────────────────────────────────────────

const TYPE_COLOR: Record<ReviewEntityType, 'default' | 'primary' | 'secondary' | 'warning' | 'info' | 'success'> = {
  wine:      'primary',
  producer:  'secondary',
  region:    'info',
  grape:     'success',
  wine_name: 'warning',
};

// ── Key fields renderer ───────────────────────────────────────────────────────

const KeyFields: React.FC<{ fields: Record<string, unknown> }> = ({ fields }) => {
  const entries = Object.entries(fields).filter(([, v]) => v != null && v !== '');
  if (entries.length === 0) return <Typography variant="caption" color="text.secondary">—</Typography>;
  return (
    <Stack spacing={0} sx={{ lineHeight: 1.4 }}>
      {entries.map(([k, v]) => (
        <Typography key={k} variant="caption" color="text.secondary">
          <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
            {k.replace(/([A-Z])/g, ' $1').toLowerCase()}:{' '}
          </Box>
          {String(v)}
        </Typography>
      ))}
    </Stack>
  );
};

// ── Summary chips ─────────────────────────────────────────────────────────────

interface SummaryBarProps {
  summary: ReviewQueueSummary;
  activeFilter: ReviewEntityType | undefined;
  onFilter: (t: ReviewEntityType | undefined) => void;
}

const SummaryBar: React.FC<SummaryBarProps> = ({ summary, activeFilter, onFilter }) => {
  const theme = useTheme();
  const chips: { label: string; type: ReviewEntityType | undefined; count: number }[] = [
    { label: 'All',       type: undefined,    count: summary.total },
    { label: 'Wine',      type: 'wine',       count: summary.wine },
    { label: 'Producer',  type: 'producer',   count: summary.producer },
    { label: 'Region',    type: 'region',     count: summary.region },
    { label: 'Grape',     type: 'grape',      count: summary.grape },
    { label: 'Wine Name', type: 'wine_name',  count: summary.wine_name },
  ];

  return (
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
      {chips.map(({ label, type, count }) => {
        const active = activeFilter === type;
        return (
          <Chip
            key={label}
            label={`${label} ${count}`}
            onClick={() => onFilter(type)}
            variant={active ? 'filled' : 'outlined'}
            color={active ? (type ? TYPE_COLOR[type] : 'primary') : 'default'}
            sx={{
              fontWeight: active ? 700 : 500,
              cursor: 'pointer',
              transition: 'all 0.15s',
              '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.08) },
            }}
          />
        );
      })}
    </Stack>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────

const ReviewQueue: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [loading, setLoading]         = useState(true);
  const [items, setItems]             = useState<ReviewQueueItem[]>([]);
  const [summary, setSummary]         = useState<ReviewQueueSummary>({
    total: 0, wine: 0, producer: 0, region: 0, grape: 0, wine_name: 0,
  });
  const [typeFilter, setTypeFilter]   = useState<ReviewEntityType | undefined>(undefined);
  const [page, setPage]               = useState(0);   // MUI 0-indexed
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalItems, setTotalItems]   = useState(0);
  const [actioningId, setActioningId] = useState<string | null>(null);

  const fetchQueue = useCallback(async () => {
    setLoading(true);
    try {
      const data = await reviewQueueService.getQueue(typeFilter, page + 1, rowsPerPage);
      setItems(data.items);
      setSummary(data.summary);
      setTotalItems(data.pagination.total);
    } catch (err) {
      console.error('Failed to fetch review queue:', err);
    } finally {
      setLoading(false);
    }
  }, [typeFilter, page, rowsPerPage]);

  useEffect(() => { fetchQueue(); }, [fetchQueue]);

  // SIMPLE-WINE.1 — a refused approve has a reason worth reading. Before
  // this, the reviewer got a console line and a row that did not move.
  const [actionError, setActionError] = useState<string>('');
  // SIMPLE-WINE.2 §4 — possible canonical matches per provisional card.
  // Fetched lazily: the queue must stay fast when most cards are not
  // provisional.
  const [matches, setMatches] = useState<Record<string, PossibleMatch[]>>({});

  const handleFilterChange = (t: ReviewEntityType | undefined) => {
    setTypeFilter(t);
    setPage(0);
  };

  const handleApprove = useCallback(async (
    item: ReviewQueueItem,
    withPendingWines = false,
  ) => {
    setActioningId(item.id);
    try {
      await reviewQueueService.approve(item.type, item.id, { withPendingWines });
      setItems(prev => prev.filter(i => i.id !== item.id));
      setSummary(prev => ({ ...prev, [item.type]: prev[item.type] - 1, total: prev.total - 1 }));
      setTotalItems(prev => prev - 1);
    } catch (err: any) {
      console.error('Approve failed:', err);
      const code = err?.response?.data?.error?.code ?? err?.response?.data?.code;
      if (code === 'PROVISIONAL_INCOMPLETE') {
        const missing = err?.response?.data?.error?.missing ?? [];
        setActionError(
          `“${item.displayName}” still needs ${missing.join(' and ') || 'a producer and region'}. `
          + 'Edit it first, then approve.',
        );
      } else if (code === 'WINE_NAME_NOT_READY') {
        // SIMPLE-WINE.2 §1 — the vintage is fine; the NAME is not reviewed.
        setActionError(
          `“${item.displayName}” hangs on a wine name that is still provisional. `
          + 'Review that name first — approving it can approve this vintage too.',
        );
      } else if (code === 'DISPLAY_NAME_CONFLICT') {
        // The cascade refused because the recomposed name lands on an
        // existing row. Renaming is not the fix — merging or relinking is.
        setActionError(
          'A wine with this name already exists — consider merging or relinking instead of renaming.',
        );
      } else {
        setActionError(`Could not approve “${item.displayName}”. Please try again.`);
      }
    } finally {
      setActioningId(null);
    }
  }, []);

  // SIMPLE-WINE.2 §4 — ask for possible canonical matches only for the cards
  // that could use one. Failing quietly is right here: the hint is a
  // convenience, and a reviewer must still be able to work without it.
  useEffect(() => {
    const provisionals = items.filter(
      (i) => i.type === 'wine_name' && (i.missingIdentity?.length ?? 0) > 0,
    );
    let cancelled = false;
    (async () => {
      for (const item of provisionals) {
        if (matches[item.id] !== undefined) continue;
        try {
          const found = await reviewQueueService.possibleMatches(item.id);
          if (!cancelled) setMatches((prev) => ({ ...prev, [item.id]: found }));
        } catch {
          if (!cancelled) setMatches((prev) => ({ ...prev, [item.id]: [] }));
        }
      }
    })();
    return () => { cancelled = true; };
  }, [items, matches]);

  // SIMPLE-WINE.3 — the suggestion no longer acts on its own. It routes into
  // the resolve view with that candidate preselected, so the reviewer sees
  // what the match would do before committing to it. The old button did the
  // whole thing behind one confirm() and could not describe the consequences.
  const handleRelink = useCallback((item: ReviewQueueItem, target: PossibleMatch) => {
    navigate(`/admin/governance/resolve/${item.id}?target=${target._id}`);
  }, [navigate]);

  const handleReject = useCallback(async (item: ReviewQueueItem) => {
    if (!window.confirm(`Reject and deactivate "${item.displayName}"?`)) return;
    setActioningId(item.id);
    try {
      await reviewQueueService.reject(item.type, item.id);
      setItems(prev => prev.filter(i => i.id !== item.id));
      setSummary(prev => ({ ...prev, [item.type]: prev[item.type] - 1, total: prev.total - 1 }));
      setTotalItems(prev => prev - 1);
    } catch (err) {
      console.error('Reject failed:', err);
    } finally {
      setActioningId(null);
    }
  }, []);

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            Review Queue
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Records created via import or auto-create that need manual review before going live.
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton onClick={fetchQueue} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 2,
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        }}
      >
        {actionError && (
          <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setActionError('')}>
            {actionError}
          </Alert>
        )}

        {/* Summary + filter chips */}
        <Box sx={{ mb: 3 }}>
          <SummaryBar summary={summary} activeFilter={typeFilter} onFilter={handleFilterChange} />
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : items.length === 0 ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              No records pending review.
            </Typography>
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, width: 110 }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Details</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: 180 }}>Source</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: 120 }}>Created</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: 130 }} align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item) => {
                    const isActioning = actioningId === item.id;
                    const canReject   = REJECT_SUPPORTED.has(item.type);
                    // SIMPLE-WINE.1 — the reason is stated on the control,
                    // not discovered by clicking it and being refused.
                    const missing = item.missingIdentity ?? [];
                    const pendingWines = item.pendingWines ?? [];
                    // SIMPLE-WINE.2 §2 — a provisional pair's primary action
                    // is not "approve", it is "go and complete this".
                    const needsCompletion = item.type === 'wine_name' && missing.length > 0;
                    const blockedReason = missing.length > 0
                      ? `Add a ${missing.join(' and ')} before approving.`
                      : '';
                    return (
                      <TableRow
                        key={item.id}
                        hover
                        sx={{
                          opacity: isActioning ? 0.5 : 1,
                          transition: 'opacity 0.2s',
                        }}
                      >
                        <TableCell>
                          <Chip
                            label={REVIEW_TYPE_LABELS[item.type]}
                            size="small"
                            color={TYPE_COLOR[item.type]}
                            variant="outlined"
                            sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {item.displayName || '(unnamed)'}
                          </Typography>
                          {/* SIMPLE-WINE.1 — this row is one line of text a
                              venue typed. It cannot be approved until someone
                              says what wine it actually is. */}
                          {(item.missingIdentity?.length ?? 0) > 0 && (
                            <Box sx={{ mt: 0.5 }}>
                              <Chip
                                label="provisional"
                                size="small"
                                color="warning"
                                variant="outlined"
                                sx={{ fontWeight: 600, fontSize: '0.65rem', mr: 0.75 }}
                              />
                              {/* SIMPLE-WINE.1 kept its promise that the reason
                                  is stated rather than discovered. §2 replaced
                                  the approve tick with the editor, so the
                                  reason moves here — off a tooltip nobody
                                  hovers and onto the card itself. */}
                              <Typography variant="caption" color="text.secondary">
                                {`Add a ${item.missingIdentity!.join(' and ')} before approving.`}
                              </Typography>
                            </Box>
                          )}

                          {/* SIMPLE-WINE.2 §4 — what a pending WINE points at.
                              Without this the reviewer cannot tell whether the
                              record behind the vintage is trustworthy. */}
                          {item.type === 'wine' && item.wineNameRef && (
                            <Box sx={{ mt: 0.5 }}>
                              <Chip
                                label={item.wineNameRef.provisional ? 'provisional name' : 'canonical name'}
                                size="small"
                                color={item.wineNameRef.provisional ? 'warning' : 'success'}
                                variant="outlined"
                                sx={{ fontWeight: 600, fontSize: '0.65rem', mr: 0.5 }}
                              />
                              <Typography variant="caption" color="text.secondary">
                                {item.wineNameRef.displayName}
                              </Typography>
                            </Box>
                          )}
                          {item.type === 'wine' && item.wineNameRef === null && (
                            <Typography variant="caption" color="error" display="block">
                              no wine name linked
                            </Typography>
                          )}

                          {/* SIMPLE-WINE.2 §2 — ONE REVIEW UNIT. The vintages
                              waiting on this name are part of the same
                              decision, so they are listed here rather than
                              sitting elsewhere in the queue with their own
                              approve ticks. */}
                          {(item.pendingWines?.length ?? 0) > 0 && (
                            <Box sx={{ mt: 1, pl: 1.5, borderLeft: '2px solid', borderColor: 'divider' }}>
                              <Typography variant="caption" color="text.secondary" display="block">
                                {item.pendingWines!.length} pending vintage
                                {item.pendingWines!.length === 1 ? '' : 's'} on this name:
                              </Typography>
                              {item.pendingWines!.map((w) => (
                                <Typography key={w.id} variant="caption" display="block" sx={{ fontWeight: 500 }}>
                                  · {String(w.keyFields?.displayVintage ?? '—')} — {w.displayName}
                                </Typography>
                              ))}
                            </Box>
                          )}

                          {/* SIMPLE-WINE.2 §4 — the cheaper correction: this
                              text already exists as a canonical record. */}
                          {(matches[item.id]?.length ?? 0) > 0 && (
                            <Box sx={{ mt: 1 }}>
                              <Typography variant="caption" color="text.secondary" display="block">
                                Possible existing match:
                              </Typography>
                              {matches[item.id].slice(0, 2).map((m) => (
                                <Stack key={m._id} direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                    {m.displayName}
                                  </Typography>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    disabled={isActioning}
                                    onClick={() => handleRelink(item, m)}
                                    sx={{ textTransform: 'none', py: 0, minWidth: 0, fontSize: '0.7rem' }}
                                  >
                                    Resolve
                                  </Button>
                                </Stack>
                              ))}
                            </Box>
                          )}
                        </TableCell>
                        <TableCell>
                          <KeyFields fields={item.keyFields} />
                        </TableCell>
                        <TableCell>
                          {/* Source chip + requester. 'admin' is the legacy /
                              default value, so we only render the chip when
                              the record came from a non-admin path. */}
                          {item.source && item.source !== 'admin' && (
                            <Chip
                              label={item.source === 'venue_request' ? 'Venue request' : 'Import'}
                              size="small"
                              color={item.source === 'venue_request' ? 'info' : 'default'}
                              variant="outlined"
                              sx={{ fontWeight: 600, fontSize: '0.65rem', mb: item.createdBy ? 0.5 : 0 }}
                            />
                          )}
                          {item.createdBy && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              by {[item.createdBy.firstName, item.createdBy.lastName].filter(Boolean).join(' ') || item.createdBy.email || '—'}
                            </Typography>
                          )}
                          {!item.source || item.source === 'admin' ? (
                            !item.createdBy && (
                              <Typography variant="caption" color="text.secondary">—</Typography>
                            )
                          ) : null}
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(item.createdAt).toLocaleDateString()}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                            {/* SIMPLE-WINE.2 §2 — while the pair is
                                incomplete the only thing worth offering is the
                                editor. An approve tick here can only ever be
                                refused, and a control that cannot succeed is
                                worse than no control. */}
                            {needsCompletion ? (
                              <Button
                                size="small"
                                variant="contained"
                                disabled={isActioning}
                                onClick={() => navigate(`/admin/governance/resolve/${item.id}`)}
                                sx={{ textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}
                              >
                                Review &amp; complete
                              </Button>
                            ) : (
                              <Tooltip
                                title={
                                  blockedReason
                                    || (pendingWines.length > 0
                                      ? `Approve — and its ${pendingWines.length} pending vintage`
                                        + `${pendingWines.length === 1 ? '' : 's'}`
                                      : 'Approve — mark as reviewed')
                                }
                              >
                                <span>
                                  <IconButton
                                    size="small"
                                    color="success"
                                    aria-label={`Approve ${item.displayName}`}
                                    onClick={() => handleApprove(item, pendingWines.length > 0)}
                                    disabled={isActioning || Boolean(blockedReason)}
                                  >
                                    <ApproveIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                            {canReject && (
                              <Tooltip title="Reject — deactivate record">
                                <span>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    aria-label={`Reject ${item.displayName}`}
                                    onClick={() => handleReject(item)}
                                    disabled={isActioning}
                                  >
                                    <RejectIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                            <Tooltip title="Edit record">
                              <IconButton
                                size="small"
                                color="primary"
                                aria-label={`Edit ${item.displayName}`}
                                onClick={() => navigate(getEditUrl(item.type, item.id))}
                                disabled={isActioning}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={totalItems}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[25, 50, 100]}
            />
          </>
        )}
      </Paper>
    </Box>
  );
};

export default ReviewQueue;
